import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../config';
import { useAuth } from '../Auth/AuthContext';

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

function addDaysIso(iso, deltaDays) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().split('T')[0];
}

async function fetchJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Request failed');
  return data;
}

export default function AdminActivityPage() {
  const { t } = useTranslation();
  const { loading: authLoading, isRoot } = useAuth();

  const defaultEnd = isoToday();
  const defaultStart = addDaysIso(defaultEnd, -30);

  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [sport, setSport] = useState('all');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const queryUrl = useMemo(() => {
    const sp = sport === 'run' || sport === 'bike' ? sport : 'all';
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    qs.set('sport', sp);
    return `${API_BASE}/admin/activity?${qs.toString()}`;
  }, [start, end, sport]);

  useEffect(() => {
    if (authLoading) return;
    if (!isRoot) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchJson(queryUrl)
      .then((data) => {
        if (cancelled) return;
        setRows(Array.isArray(data?.users) ? data.users : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load');
        setRows([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isRoot, queryUrl]);

  if (authLoading) {
    return <div className="p-6 text-sm text-gray-600">Loading…</div>;
  }
  if (!isRoot) {
    return <div className="p-6 text-sm text-red-600 font-bold">Forbidden</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            {t('admin.activity.title')}
          </h1>
          <div className="text-sm text-gray-600 mt-1">
            {t('admin.activity.subtitle')}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500 mb-1">
              {t('admin.activity.start')}
            </div>
            <input
              type="date"
              value={start || ''}
              onChange={(e) => setStart(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold"
            />
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500 mb-1">
              {t('admin.activity.end')}
            </div>
            <input
              type="date"
              value={end || ''}
              onChange={(e) => setEnd(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold"
            />
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500 mb-1">
              {t('admin.activity.sport')}
            </div>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold bg-white"
            >
              <option value="all">{t('admin.activity.sport_all')}</option>
              <option value="run">{t('admin.activity.sport_run')}</option>
              <option value="bike">{t('admin.activity.sport_bike')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-sm text-gray-600">Loading…</div>
        ) : error ? (
          <div className="text-sm text-red-600 font-bold">{error}</div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600">
                      {t('admin.activity.user')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.plans')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.workouts')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.modifications')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.strava_formatted')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.coach_messages')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.ratings_up')}
                    </th>
                    <th className="px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-gray-600 text-right">
                      {t('admin.activity.ratings_down')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const label = u.display_name || u.email || u.id;
                    return (
                      <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-bold text-gray-900 truncate max-w-[360px]">
                            {label}
                          </div>
                          {u.email ? (
                            <div className="text-[11px] text-gray-500 font-mono truncate max-w-[360px]">
                              {u.email}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.plans_count}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.workouts_done_count}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.plan_modifications_count}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums text-orange-600">{u.strava_formatted_count}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.coach_messages_count}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.coach_rating_up_count ?? 0}</td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums">{u.coach_rating_down_count ?? 0}</td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-sm text-gray-500" colSpan={8}>
                        {t('admin.activity.empty')}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


